'use client';

import { useQuery } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { formatISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

import { getAgreementDetailsApi } from '@flipster/apis/v1/agreement';
import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { Spinner } from '@flipster/react/design-system/spinner';
import UserCautionColorIcon from '@flipster/react/icon/generated/UserCautionColorIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';
import { getErrorMessage } from '@flipster/utils/error';
import { deleteLocalStorage, getFlipsterConfigsV1Key, setLocalStorage } from '@flipster/utils/localStorage';
import segment, { TncTransitionEvent, TncTransitionFromRoute } from '@flipster/utils/segment';

import { PolicyAgreementCheckbox } from '../../(session)/_policy/PolicyAgreementCheckbox';
import { varaTransitionAgreementBunja } from './bunja';

interface VaraTransitionAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  from?: TncTransitionFromRoute;
  onExit: () => void;
}

export const VaraTransitionAgreementModal = ({ isOpen, onClose, from, onExit }: VaraTransitionAgreementModalProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const transCommon = useTranslations('Common.r250804');
  const { refetch: refetchKycInfo } = useKYC();
  const { postTncAgreementApiAtom } = useBunja(varaTransitionAgreementBunja);
  const { mutateAsync } = useAtomValue(postTncAgreementApiAtom);
  const [isChecked, setIsChecked] = useState(false);
  const { data: agreementDetails, isLoading } = useQuery({
    queryKey: ['getAgreementDetailsApi'],
    queryFn: () => getAgreementDetailsApi({ agreementType: 'UPDATE_TNC' }),
    select: (data) => data.data,
  });

  const overrideOnClose = () => {
    if (user?.userId)
      setLocalStorage(getFlipsterConfigsV1Key(user.userId, 'tncAgreementModalHideTimestamp'), formatISO(new Date()));
    onClose();
  };

  const handleAccept = async () => {
    if (!agreementDetails?.key) return;
    try {
      await mutateAsync(agreementDetails.key);
      segment.track(TncTransitionEvent.TncTransitionModalAcceptClick);
      if (user?.userId) deleteLocalStorage(getFlipsterConfigsV1Key(user.userId, 'tncAgreementModalHideTimestamp'));
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
      router.push('/user/kyc');
    } finally {
      await refetchKycInfo();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} css={{ minH: 360 }} onExit={onExit}>
      <SegmentPage name={TncTransitionEvent.TncTransitionModalView} context={{ from_route: from }} />
      <Modal.Close onClose={overrideOnClose} />
      {isLoading ? (
        <div className={center({ flex: 1 })}>
          <Spinner size={36} />
        </div>
      ) : (
        <>
          <Modal.Header>
            <Modal.Title
              css={hstack.raw({ gap: 8, alignItems: 'flex-start' })}
              label={
                <>
                  <UserCautionColorIcon size={24} className={css({ flexShrink: 0 })} />
                  {agreementDetails?.title}
                </>
              }
            />
          </Modal.Header>
          <Modal.Content>
            <div className={stack({ gap: 20, h: '100%', justifyContent: 'space-between' })}>
              <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
                {agreementDetails?.desc}
              </p>
              <PolicyAgreementCheckbox
                agreementType='UPDATE_TNC'
                forceNewTab
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
            </div>
          </Modal.Content>
          <Modal.Footer>
            <Button
              hierarchy='primary'
              size='large'
              label={transCommon('Accept')}
              disabled={!isChecked}
              onClick={handleAccept}
            />
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};
